import { CreateScenarioRequest, MetadataEntityType, Model, NonModelledElementsStatus, RebarEstimationStatus, Scenario, ScenarioMetadata } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useProject } from "@api/projects";
import { useCreateScenario, useScenarios } from "@api/scenarios";
import MicroChip from "@components/MicroChip/MicroChip";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Card, CardContent, CardHeader, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Switch, Typography, useTheme } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import _ from "lodash";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ScenarioMetadataForm } from "./ScenarioMetadataForm";

// New Import
import { DropDownOption } from "@/types/dropdown";
import { useProfiles } from "@api/profiles";
import { useAddNonModelledMaterials } from "@api/nonModelledFamily";
import { MetadataEntityResponse } from "@api/apiClient";
import { queryClient } from "@hooks/useGlobalState";

export interface ScenariosTabProps {
  open: boolean;
  onClose: () => void;
}

const AddScenarioDialog = observer((props: ScenariosTabProps) => {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const theme = useTheme();
  const { id: projectId } = useParams();
  const project = useProject(projectId!);
  const scenarios = useScenarios(projectId!);
  const createScenario = useCreateScenario();
  const appSettings = useAppSettings();
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();

  const [formData, setFormData] = useState<CreateScenarioRequest>(
    new CreateScenarioRequest({
      projectId,
      name: "",
      description: "",
      rebarEstimation: false,
      nonModelledElements: false,
      projectPhaseId: "",
      isFinalforProjectPhase: false,
    } as any),
  );

  const [metadata, setMetadata] = useState<ScenarioMetadata>(
    new ScenarioMetadata({
      totalFloorArea: project.data?.project?.totalFloorArea,
      serviceLife: project.data?.project?.serviceLife,
    }),
  );

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [duplicateScenarioError, setDuplicateScenarioError] = useState(false);
  const [modelSelectionError, setModelSelectionError] = useState(false);

  // === NEW: Profile State ===
  const { data: profileData } = useProfiles();
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [profileOptions, setProfileOptions] = useState<{ label: string; value: string }[]>([]);
  const [projectPhaseOptions, setProjectPhaseOptions] = useState<DropDownOption[]>([]);
  useEffect(() => {
    // Filter for ProjectPhase entities and map to dropdown options
    const options: DropDownOption[] =
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => entity.type === MetadataEntityType.ProjectPhase)
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [];
    setProjectPhaseOptions(options);
  }, [appStore.applicationSettings?.metadataEntities]);

  // === Asset State ===
  const [assetOptions, setAssetOptions] = useState<DropDownOption[]>([]);
  const [selectedAssetValue, setSelectedAssetValue] = useState<string>("");
  const createAsset = useAddNonModelledMaterials("Asset") as any;
  
  useEffect(() => {
    // Filter for Asset entities and map to dropdown options
    const options: DropDownOption[] =
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => (entity.type as string) === "Asset")
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [];
    setAssetOptions(options);
  }, [appStore.applicationSettings?.metadataEntities]);

  useEffect(() => {
    if (project.data) {
      const createScenarioInput = new CreateScenarioRequest({
        projectId,
        name: "",
        description: "",
        rebarEstimation:
          project.data?.project?.rebarEstimationStatus === RebarEstimationStatus.Enabled || project.data?.project?.rebarEstimationStatus === RebarEstimationStatus.Estimated,
        nonModelledElements:
          project.data?.project?.nonModelledElementsStatus === NonModelledElementsStatus.Enabled ||
          project.data?.project?.nonModelledElementsStatus === NonModelledElementsStatus.Estimated,
        modelIds: selectionManager.selectedProject?.models?.map((model: Model) => model.modelId!) || [],
      });
      setFormData(createScenarioInput);
    }
  }, [project.data]);

  // === NEW: Populate profile dropdown ===
  useEffect(() => {
    if (!appSettings.data?.profileLevelRebarSettings) return;

    if (profileData && profileData.length > 0) {
      const _profileOptions = profileData.map((profile) => ({
        label: profile.name ?? "",
        value: profile.profileId ?? "",
      }));

      setProfileOptions([{ label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" }, ..._profileOptions]);

      const projectProfileId = project.data?.project?.profile?.profileId ?? "";
      const isValidProfile = _profileOptions.some((opt) => opt.value === projectProfileId);

      setSelectedProfileValue(isValidProfile ? projectProfileId : (_profileOptions[0]?.value ?? ""));
    } else {
      setProfileOptions([{ label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" }]);
      setSelectedProfileValue("");
    }
  }, [profileData, project.data]);

  const handleInputChange = (event: any) => {
    setFormData(new CreateScenarioRequest({ ...formData, [event.target.name]: event.target.value }));
    if (event.target.name === "name") {
      const hasMatch = scenarios.data?.find((x: Scenario) => x.name?.toLocaleLowerCase() === event.target.value.trim().toLocaleLowerCase());
      setDuplicateScenarioError(hasMatch !== undefined);
    }
  };

  const handleMetadataChange = (field: string, value: number | undefined) => {
    setMetadata((prev) => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        [field]: value,
      };
      return new ScenarioMetadata(newData);
    });
  };

  const handleModelInputChange = (_event: any, value: any) => {
    const modelIds = value?.map((v: Model) => v.modelId);
    setModelSelectionError(modelIds?.length === 0);
    setFormData(new CreateScenarioRequest({ ...formData, modelIds }));
  };

  const handleRebarEstimationToggle = () => {
    setFormData({ ...formData, rebarEstimation: !formData.rebarEstimation } as any);
  };

  const handleCreateAsset = async (assetName: string) => {
    if (!assetName.trim()) return;
    
    try {
      const created = await createAsset.mutateAsync({ value: assetName.trim() }) as MetadataEntityResponse;
      
      // Invalidate and refetch appSettings to refresh the metadata entities
      await queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      await appSettings.refetch();
      
      // Add the newly created asset to options immediately
      const newOption = { key: created.metadataEntityId!, label: created.name! };
      setAssetOptions((prev) => {
        if (!prev.find(opt => opt.key === newOption.key)) {
          return [...prev, newOption];
        }
        return prev;
      });
      
      setSelectedAssetValue(created.metadataEntityId!);
      setFormData(new CreateScenarioRequest({ ...formData, assetId: created.metadataEntityId! } as any));
    } catch (error) {
      console.error("Failed to create asset:", error);
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const modelIds = formData.modelIds ?? [];
    if (formData.name?.trim().length !== 0 && modelIds.length > 0) {
      try {
        setIsSubmitted(true);
        await handleCreateScenario(formData);
      } finally {
        setIsSubmitted(false);
      }
    }
  };

  const handleCreateScenario = async (formData: any) => {
    const createScenarioInput: any = new CreateScenarioRequest({
      ...formData,
      profileId: selectedProfileValue, // NEW: include selected profile
      assetId: selectedAssetValue || undefined, // Include selected asset
    } as any);

    if (showMetadata) {
      const scenarioMetadata = new ScenarioMetadata({
        totalFloorArea: metadata.totalFloorArea,
        serviceLife: metadata.serviceLife,
      });

      (createScenarioInput as CreateScenarioRequest).scenarioMetadata = scenarioMetadata;
    }

    await createScenario.mutateAsync(createScenarioInput);
    handleClose();
  };

  const handleClose = () => {
    setDuplicateScenarioError(false);
    props.onClose();
  };

  return (
    <Dialog fullWidth={true} maxWidth={"sm"} open={props.open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {i18n.t("scenarios-tab.scenario-dialog.title")}
        <IconButton onClick={handleClose} disabled={isSubmitted}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Stack mt={1} spacing={2}>
            <TextField
              label={i18n.t("scenarios-tab.scenario-dialog.name-input.title")}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              fullWidth
              required
              disabled={isSubmitted}
              error={(isSubmitted && _.isEmpty(formData.name?.trim())) || duplicateScenarioError}
              helperText={
                isSubmitted && _.isEmpty(formData.name?.trim())
                  ? i18n.t("scenario.helper.scenario-name")
                  : duplicateScenarioError
                    ? i18n.t("scenarios-tab.scenario-dialog.name-input.duplicate")
                    : ""
              }
            />
            <TextField
              label={i18n.t("scenarios-tab.scenario-dialog.description-input.title")}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              fullWidth
              required
              disabled={isSubmitted}
              error={isSubmitted && _.isEmpty(formData.description?.trim())}
              helperText={isSubmitted && _.isEmpty(formData.description?.trim()) ? i18n.t("scenario.helper.scenario-description") : ""}
            />

            {/* Additional Metadata Fields */}
            {showMetadata && <ScenarioMetadataForm metadata={metadata} onChange={handleMetadataChange} />}

            {/* Profile Dropdown */}
            {appSettings.data?.profileLevelRebarSettings && (
              <FormControl fullWidth margin="normal" disabled={isSubmitted}>
                <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}</InputLabel>
                <Select value={selectedProfileValue} label={i18n.t("scenarios-tab.scenario-dialog.profile-input.title")} onChange={(e) => setSelectedProfileValue(e.target.value)}>
                  {profileOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={selectionManager.selectedProject?.models || []}
              getOptionKey={(option: Model) => option.modelId!}
              getOptionLabel={(option: any) => `${option?.modelName} (${option?.modelVersion})`}
              defaultValue={selectionManager.selectedProject?.models || []}
              isOptionEqualToValue={(option, value) => option.modelId === value.modelId}
              renderOption={(props: any, option: Model, { selected }) => (
                <Box
                  {...props}
                  key={option.modelId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px",
                    alignItems: "center",
                    minWidth: "200px",
                  }}
                >
                  <Checkbox checked={selected} />
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">{option?.modelName}</Typography>
                    <MicroChip label={option?.modelVersion || ""} />
                  </Box>
                </Box>
              )}
              onChange={handleModelInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("scenarios-tab.scenario-dialog.models-select.input.title")}
                  placeholder={i18n.t("scenarios-tab.scenario-dialog.models-select.input.placeholder")}
                  error={modelSelectionError}
                  helperText={modelSelectionError ? i18n.t("scenarios-tab.scenario-dialog.models-select.input.helper-text") : ""}
                />
              )}
              disabled={isSubmitted}
              renderTags={(value, getTagProps) => {
                return value.map((option: Model, index: number) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.modelId}
                    label={
                      <>
                        {option?.modelName}
                        <MicroChip label={option?.modelVersion || ""} sx={{ marginLeft: 1, height: "20px" }} />
                      </>
                    }
                    sx={{ alignItems: "center" }}
                  />
                ));
              }}
            />

            <Autocomplete
              freeSolo
              options={assetOptions}
              value={selectedAssetValue ? assetOptions.find(opt => opt.key === selectedAssetValue) || null : null}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                return option?.label || "";
              }}
              filterOptions={(options, params) => {
                const filtered = createFilterOptions<DropDownOption>()(options, params);
                return filtered;
              }}
              onChange={async (_event, newValue) => {
                if (!newValue) {
                  setSelectedAssetValue("");
                  setFormData(new CreateScenarioRequest({ ...formData, assetId: "" } as any));
                  return;
                }
                
                // Handle existing option selection
                if (typeof newValue === "object" && newValue.key) {
                  setSelectedAssetValue(newValue.key);
                  setFormData(new CreateScenarioRequest({ ...formData, assetId: newValue.key } as any));
                }
              }}
              renderInput={(params) => {
                const currentInputValue = params.inputProps.value as string || "";
                const selectedAssetLabel = selectedAssetValue 
                  ? assetOptions.find(opt => opt.key === selectedAssetValue)?.label || ""
                  : "";
                const isTypedValueNew = currentInputValue.trim() !== "" && 
                  !assetOptions.some(opt => opt.label.toLowerCase() === currentInputValue.toLowerCase()) &&
                  currentInputValue !== selectedAssetLabel;
                
                return (
                  <TextField
                    {...params}
                    label="Asset"
                    placeholder="Type to search or add new asset"
                    disabled={isSubmitted || createAsset.isPending}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isTypedValueNew && (
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (currentInputValue.trim()) {
                                  await handleCreateAsset(currentInputValue.trim());
                                }
                              }}
                              disabled={createAsset.isPending}
                              sx={{ marginRight: 1 }}
                            >
                              <AddIcon fontSize="small" color="primary" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              disabled={isSubmitted || createAsset.isPending}
              loading={createAsset.isPending}
            />

            <FormControl fullWidth>
              <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}</InputLabel>
              <Select
                value={formData.projectPhaseId ?? ""}
                label="Project Phase"
                onChange={(event) => {
                  setFormData(new CreateScenarioRequest({ ...formData, projectPhaseId: event.target.value as string }));
                }}
              >
                {projectPhaseOptions.map((option: DropDownOption) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
              {(formData.projectPhaseId || (!appSettings.data?.profileLevelRebarSettings && appSettings.data?.rebarEstimation)) && (
                <CardHeader title={<Typography variant="body2">{i18n.t("scenarios-tab.title.scenario-settings")}</Typography>} elevation={3} />
              )}
              {!appSettings.data?.profileLevelRebarSettings && appSettings.data?.rebarEstimation && (
                <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{i18n.t("scenarios-tab.title.rebar-estimation")}</Typography>
                    <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                      {i18n.t("scenarios-tab.description.rebar-estimation")}
                    </Typography>
                  </Box>
                  <Switch checked={formData.rebarEstimation} onChange={handleRebarEstimationToggle} disabled={isSubmitted} />
                </CardContent>
              )}
              {formData.projectPhaseId && (
                <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{i18n.t("scenarios-tab.scenario-dialog.final-for-phase.title")}</Typography>
                    {formData.isFinalforProjectPhase &&
                      formData.projectPhaseId &&
                      (() => {
                        const existingFinalScenario = scenarios.data?.find((s: Scenario) => s.projectPhaseId === formData.projectPhaseId && s.isFinalForProjectPhase === true);
                        return existingFinalScenario ? (
                          <Typography variant="caption" color="warning.main">
                            {i18n.t("scenarios-tab.scenario-dialog.final-for-phase-disclaimer.title", { projectPhase: formData.projectPhaseId })}
                          </Typography>
                        ) : null;
                      })()}
                  </Box>
                  <Switch
                    checked={formData.isFinalforProjectPhase}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setFormData(new CreateScenarioRequest({ ...formData, isFinalforProjectPhase: newValue } as any));
                    }}
                    disabled={isSubmitted}
                  />
                </CardContent>
              )}
            </Card>

            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ justifyContent: "end" }}>
                <Grid>
                  <Button variant="outlined" onClick={handleClose} disabled={isSubmitted}>
                    {i18n.t("scenarios-tab.scenario-dialog.close-button.title")}
                  </Button>
                </Grid>
                <Grid>
                  <Button autoFocus loading={isSubmitted} disabled={duplicateScenarioError} variant="contained" type="submit">
                    {i18n.t("scenarios-tab.scenario-dialog.save-button.title")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default AddScenarioDialog;
